import { useState, useCallback } from "react";

const STORAGE_KEY = "wz-timezone";

function getBrowserTimezone(): string {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone;
  } catch {
    return "UTC";
  }
}

function getStoredTimezone(): string {
  try {
    return localStorage.getItem(STORAGE_KEY) || getBrowserTimezone();
  } catch {
    return getBrowserTimezone();
  }
}

/**
 * Get the UTC offset in hours for a given IANA timezone.
 * Positive = east of UTC, negative = west.
 */
export function getTimezoneOffsetHours(tz: string): number {
  try {
    const now = new Date();
    // Format a date in the target timezone and in UTC, then compare
    const utcParts = new Intl.DateTimeFormat("en-US", {
      timeZone: "UTC",
      hour: "numeric",
      minute: "numeric",
      hour12: false,
      year: "numeric",
      month: "numeric",
      day: "numeric",
    }).formatToParts(now);

    const tzParts = new Intl.DateTimeFormat("en-US", {
      timeZone: tz,
      hour: "numeric",
      minute: "numeric",
      hour12: false,
      year: "numeric",
      month: "numeric",
      day: "numeric",
    }).formatToParts(now);

    const get = (parts: Intl.DateTimeFormatPart[], type: string) =>
      parseInt(parts.find((p) => p.type === type)?.value ?? "0");

    const utcH = get(utcParts, "hour");
    const utcM = get(utcParts, "minute");
    const utcD = get(utcParts, "day");

    const tzH = get(tzParts, "hour");
    const tzM = get(tzParts, "minute");
    const tzD = get(tzParts, "day");

    let diffHours = tzH - utcH + (tzM - utcM) / 60;

    // Handle day boundary
    if (tzD !== utcD) {
      if (tzD > utcD || (utcD > 27 && tzD < 3)) diffHours += 24;
      else if (utcD > tzD || (tzD > 27 && utcD < 3)) diffHours -= 24;
    }

    return diffHours;
  } catch {
    return 0;
  }
}

/**
 * Short label for a timezone, e.g. "PST", "EST", "UTC+5:30"
 */
export function timezoneLabel(tz: string): string {
  if (tz === "UTC") return "UTC";
  try {
    const fmt = new Intl.DateTimeFormat("en-US", {
      timeZone: tz,
      timeZoneName: "short",
    });
    const parts = fmt.formatToParts(new Date());
    return parts.find((p) => p.type === "timeZoneName")?.value ?? tz;
  } catch {
    return tz;
  }
}

/**
 * Shift a UTC heatmap (hour 0-23, day 0-6 Mon-Sun) by timezone offset.
 */
export function shiftHeatmap(
  data: Array<{ hour: number; day: number; count: number }>,
  offsetHours: number,
): Array<{ hour: number; day: number; count: number }> {
  if (offsetHours === 0) return data;

  // Build a full 7×24 grid from the data
  const grid: number[][] = Array.from({ length: 7 }, () => Array(24).fill(0));
  for (const d of data) {
    if (d.day >= 0 && d.day < 7 && d.hour >= 0 && d.hour < 24) {
      grid[d.day][d.hour] = d.count;
    }
  }

  // Shift and rebuild
  const shifted: Array<{ hour: number; day: number; count: number }> = [];
  for (let day = 0; day < 7; day++) {
    for (let hour = 0; hour < 24; hour++) {
      let srcHour = hour - Math.floor(offsetHours);
      let srcDay = day;
      const fracMinutes = Math.round((offsetHours % 1) * 60);
      if (fracMinutes !== 0) {
        // For half-hour offsets, approximate by rounding
        srcHour = hour - Math.round(offsetHours);
      }

      if (srcHour < 0) {
        srcHour += 24;
        srcDay = (srcDay - 1 + 7) % 7;
      } else if (srcHour >= 24) {
        srcHour -= 24;
        srcDay = (srcDay + 1) % 7;
      }

      const count = grid[srcDay]?.[srcHour] ?? 0;
      if (count > 0) {
        shifted.push({ hour, day, count });
      }
    }
  }

  return shifted;
}

/**
 * Hook to manage timezone selection with localStorage persistence.
 * Defaults to the browser's timezone.
 */
export function useTimezone() {
  const [tz, setTzState] = useState(getStoredTimezone);

  const setTimezone = useCallback((newTz: string) => {
    setTzState(newTz);
    try {
      localStorage.setItem(STORAGE_KEY, newTz);
    } catch {}
  }, []);

  const toggleUtc = useCallback(() => {
    setTimezone(tz === "UTC" ? getBrowserTimezone() : "UTC");
  }, [tz, setTimezone]);

  const browserTz = getBrowserTimezone();

  return { tz, setTimezone, toggleUtc, browserTz, isUtc: tz === "UTC" };
}
